import { useState } from 'react';

import {
	AddToCartBody,
	IAddToCart,
	IAddToCartResult,
	ICart,
	RoutePath,
} from '@quizzach/interfaces';
import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';
import { useUserAuthentication } from '@quizzach/hooks';
import { useCartContext } from '@quizzach/contexts';
import { useNavigate } from 'react-router-dom';

export const useAddToCart = ({ type, uuid }: IAddToCart) => {
	const [error, setError] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const { user } = useUserAuthentication();
	const { refreshCartCount } = useCartContext();
	const navigate = useNavigate();

	const checkType = (): AddToCartBody | undefined => {
		if (type === 'quick') {
			const quickQuizData = JSON.parse(
				window.sessionStorage.getItem('quickQuiz') || '{}'
			);
			const { number_of_questions, question_types } = quickQuizData;

			if (!number_of_questions || !question_types) return undefined;

			return {
				quick_quiz: {
					number_of_questions,
					question_types,
				},
			};
		}
		if (type === 'premade') {
			return { premade_quiz_uuid: uuid };
		}
	};

	const addToLoggedInUserCart = async (
		item: AddToCartBody
	): Promise<IAddToCartResult> => {
		setLoading(true);
		try {
			const response = await fetchData<ICart>(
				`/api/cart/items`,
				{
					method: 'POST',
					body: JSON.stringify(item),
				},
				{ authRoute: true }
			);

			if (response.status < 300) {
				consoleDev('addToLoggedInUserCart', ConsoleStatus.SUCC);
				sessionStorage.removeItem('quickQuiz');
				setError(false);
				refreshCartCount();
				return { success: true };
			} else if (response.status === 422) {
				// do not show error toast message when user double adds the quiz in the cart
				setError(false);
				return { success: true };
			} else if (response.status === 409) {
				// the quiz can't be generated with the requested conditions
				consoleDev(
					'addToLoggedInUserCart',
					ConsoleStatus.HTTP,
					response.status
				);
				setError(true);
				return { success: false, conflict: true };
			} else {
				consoleDev(
					'addToLoggedInUserCart',
					ConsoleStatus.HTTP,
					response.status
				);
				setError(true);
				return { success: false };
			}
		} catch (err) {
			consoleDev('addToLoggedInUserCart', ConsoleStatus.NET, err);
			setError(true);
			return { success: false };
		} finally {
			setLoading(false);
		}
	};

	const addToCart = async (): Promise<IAddToCartResult> => {
		if (user) {
			const item = checkType();
			if (item) {
				return await addToLoggedInUserCart(item);
			} else {
				console.error('Error adding to the cart: Invalid item');
				setError(true);
				return { success: false };
			}
		} else {
			// after login the user lands back on the pick page with their
			// picks restored (quickQuiz sessionStorage) and continues themselves
			navigate(RoutePath.Login, {
				state: { source: 'quick', google_referer: RoutePath.QuickPick },
			});
			return { success: false, redirectedToLogin: true };
		}
	};

	return {
		addToCart,
		error,
		loading,
	};
};
